import { useState, useRef, useCallback, useEffect } from "react";
import type {
  ReplaySession,
  ReplayPhase,
  SearchQuery,
  Reference,
} from "../data/types";

interface ParsedCode {
  language: string;
  code: string;
}

function parseExampleContent(content: string): ParsedCode {
  if (!content) return { language: "text", code: "No example available" };
  const match = content.match(/^```(\w+)\n([\s\S]*?)\n```$/m);
  if (match) {
    return { language: match[1], code: match[2] };
  }
  return { language: "text", code: content };
}

/** Strip the leading markdown heading (## Title) since exampleTitle already shows it */
function stripLeadingHeading(text: string): string {
  return text.replace(/^##\s+[^\n]+\n+/, "");
}

export interface UseReplayReturn {
  phase: ReplayPhase;
  displayedQuery: string;
  cursorVisible: boolean;
  visibleSearchQueries: SearchQuery[];
  visibleReferences: Reference[];
  displayedCode: string;
  codeLanguage: string;
  exampleTitle: string;
  explanation: string;
  start: (session: ReplaySession) => void;
  reset: () => void;
  activeSession: ReplaySession | null;
  language: string | null;
}

export function useReplay(): UseReplayReturn {
  const [phase, setPhase] = useState<ReplayPhase>("idle");
  const [displayedQuery, setDisplayedQuery] = useState("");
  const [cursorVisible, setCursorVisible] = useState(false);
  const [visibleSearchQueries, setVisibleSearchQueries] = useState<
    SearchQuery[]
  >([]);
  const [visibleReferences, setVisibleReferences] = useState<Reference[]>([]);
  const [displayedCode, setDisplayedCode] = useState("");
  const [codeLanguage, setCodeLanguage] = useState("text");
  const [exampleTitle, setExampleTitle] = useState("");
  const [explanation, setExplanation] = useState("");
  const [activeSession, setActiveSession] = useState<ReplaySession | null>(
    null
  );

  const runIdRef = useRef(0);
  const timersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  const addTimer = useCallback(
    (fn: () => void, delay: number): ReturnType<typeof setTimeout> => {
      const id = setTimeout(() => {
        timersRef.current.delete(id);
        fn();
      }, delay);
      timersRef.current.add(id);
      return id;
    },
    []
  );

  const reset = useCallback(() => {
    runIdRef.current++;
    timersRef.current.forEach((id) => clearTimeout(id));
    timersRef.current.clear();
    setPhase("idle");
    setDisplayedQuery("");
    setCursorVisible(false);
    setVisibleSearchQueries([]);
    setVisibleReferences([]);
    setDisplayedCode("");
    setCodeLanguage("text");
    setExampleTitle("");
    setExplanation("");
    setActiveSession(null);
  }, []);

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((id) => clearTimeout(id));
      timers.clear();
    };
  }, []);

  const start = useCallback(
    (session: ReplaySession) => {
      // Reset everything first
      timersRef.current.forEach((id) => clearTimeout(id));
      timersRef.current.clear();
      runIdRef.current++;
      const currentRunId = runIdRef.current;

      const isStale = () => runIdRef.current !== currentRunId;

      setDisplayedQuery("");
      setCursorVisible(true);
      setVisibleSearchQueries([]);
      setVisibleReferences([]);
      setDisplayedCode("");
      setExampleTitle(session.example?.title ?? "");
      setExplanation("");
      setActiveSession(session);

      const parsed = parseExampleContent(session.example?.content ?? "");
      setCodeLanguage(parsed.language);

      const fullExplanation = stripLeadingHeading(
        session.example?.explanation ?? ""
      );

      // Skip animations for users who prefer reduced motion
      const prefersReducedMotion =
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      if (prefersReducedMotion) {
        setDisplayedQuery(session.query.replace(/\s+/g, " ").trim());
        setCursorVisible(false);
        setVisibleSearchQueries(session.search_queries);
        setVisibleReferences(session.references);
        setDisplayedCode(parsed.code || "No example available");
        setExplanation(fullExplanation);
        setPhase("done");
        return;
      }

      // Phase 1: Typing
      setPhase("typing");

      // Normalize whitespace: some queries contain embedded newlines + indentation
      const query = session.query.replace(/\s+/g, " ").trim();

      // Phase 1b: Submitting (button pulse + bar exit animation)
      const startSubmitting = () => {
        if (isStale()) return;
        setCursorVisible(false);
        setPhase("submitting");
        // 700ms for the button pulse (200ms) + slide-up exit (500ms)
        addTimer(() => {
          if (isStale()) return;
          startSearching();
        }, 700);
      };

      const kickOffTyping = () => {
        if (query.length === 0) {
          // Skip typing for empty queries (avoids division by zero)
          setCursorVisible(false);
          addTimer(() => {
            if (isStale()) return;
            startSubmitting();
          }, 100);
          return;
        }

        const TYPING_MAX_MS = 3000;
        const BASE_MS_PER_CHAR = 35;
        const totalTypingTime = Math.min(
          query.length * BASE_MS_PER_CHAR,
          TYPING_MAX_MS
        );
        // msPerChar: target ms per character, adjusted so total stays within cap
        const msPerChar = totalTypingTime / query.length;
        // charsPerTick: how many chars to advance per timer fire,
        // so the actual timer interval stays close to BASE_MS_PER_CHAR (~35ms)
        const charsPerTick = Math.max(
          1,
          Math.ceil(BASE_MS_PER_CHAR / msPerChar)
        );
        const typingIntervalMs = msPerChar * charsPerTick;

        let charIndex = 0;

        const typeNextChars = () => {
          if (isStale()) return;
          charIndex = Math.min(charIndex + charsPerTick, query.length);
          setDisplayedQuery(query.slice(0, charIndex));
          if (charIndex < query.length) {
            addTimer(typeNextChars, typingIntervalMs);
          } else {
            // Typing done, brief pause then submit
            addTimer(() => {
              if (isStale()) return;
              startSubmitting();
            }, 200);
          }
        };

        addTimer(typeNextChars, 100);
      };

      // Phase 2: Searching
      const startSearching = () => {
        if (isStale()) return;
        const queries = session.search_queries;
        if (queries.length === 0) {
          startAnalyzing();
          return;
        }
        setPhase("searching");
        setCursorVisible(false);

        const SEARCH_MAX_MS = 2000;
        const baseDelay = 400;
        const delay = Math.min(baseDelay, SEARCH_MAX_MS / queries.length);

        let queryIndex = 0;
        const showNextQuery = () => {
          if (isStale()) return;
          queryIndex++;
          setVisibleSearchQueries(queries.slice(0, queryIndex));
          if (queryIndex < queries.length) {
            addTimer(showNextQuery, delay);
          } else {
            addTimer(() => {
              if (isStale()) return;
              startAnalyzing();
            }, 400);
          }
        };
        addTimer(showNextQuery, delay);
      };

      // Phase 3: Analyzing (reveal references)
      const startAnalyzing = () => {
        if (isStale()) return;
        const refs = session.references;
        if (refs.length === 0) {
          startGenerating(parsed);
          return;
        }
        setPhase("analyzing");

        let refIndex = 0;
        const showNextRef = () => {
          if (isStale()) return;
          refIndex++;
          setVisibleReferences(refs.slice(0, refIndex));
          if (refIndex < refs.length) {
            const delay = refIndex <= 3 ? 400 : 150;
            addTimer(showNextRef, delay);
          } else {
            addTimer(() => {
              if (isStale()) return;
              startGenerating(parsed);
            }, 500);
          }
        };
        addTimer(showNextRef, 300);
      };

      // Phase 4: Generating (stream code line-by-line)
      const startGenerating = (parsed: ParsedCode) => {
        if (isStale()) return;
        setPhase("generating");

        const lines = parsed.code.split("\n");
        if (lines.length === 0 || (lines.length === 1 && !lines[0])) {
          setDisplayedCode("No example available");
          addTimer(() => {
            if (isStale()) return;
            startExplaining();
          }, 300);
          return;
        }

        const CODE_MAX_MS = 4000;
        const BASE_MS_PER_LINE = 60;
        const totalCodeTime = Math.min(
          lines.length * BASE_MS_PER_LINE,
          CODE_MAX_MS
        );
        const msPerLine = totalCodeTime / lines.length;
        const linesPerTick = Math.max(
          1,
          Math.ceil(BASE_MS_PER_LINE / msPerLine)
        );
        const codeIntervalMs = msPerLine * linesPerTick;

        let lineIndex = 0;

        const showNextLines = () => {
          if (isStale()) return;
          lineIndex = Math.min(lineIndex + linesPerTick, lines.length);
          setDisplayedCode(lines.slice(0, lineIndex).join("\n"));
          if (lineIndex < lines.length) {
            addTimer(showNextLines, codeIntervalMs);
          } else {
            addTimer(() => {
              if (isStale()) return;
              startExplaining();
            }, 300);
          }
        };
        addTimer(showNextLines, 100);
      };

      // Phase 5: Stream explanation with token-like character chunks
      const startExplaining = () => {
        if (isStale()) return;
        setPhase("done");

        if (!fullExplanation) return;

        const EXPLAIN_MAX_MS = 8000;
        const BASE_MS_PER_CHUNK = 40;
        const CHARS_PER_CHUNK = 12;
        const totalChunks = Math.ceil(fullExplanation.length / CHARS_PER_CHUNK);
        const totalTime = Math.min(
          totalChunks * BASE_MS_PER_CHUNK,
          EXPLAIN_MAX_MS
        );
        const msPerChunk = totalTime / totalChunks;
        const charsPerTick = Math.max(
          CHARS_PER_CHUNK,
          Math.ceil((fullExplanation.length * BASE_MS_PER_CHUNK) / EXPLAIN_MAX_MS)
        );
        const intervalMs = Math.max(30, msPerChunk * (charsPerTick / CHARS_PER_CHUNK));

        let charIndex = 0;

        const showNextChunk = () => {
          if (isStale()) return;
          charIndex = Math.min(charIndex + charsPerTick, fullExplanation.length);
          setExplanation(fullExplanation.slice(0, charIndex));
          if (charIndex < fullExplanation.length) {
            addTimer(showNextChunk, intervalMs);
          }
        };
        addTimer(showNextChunk, 300);
      };

      // Start the animation chain
      kickOffTyping();
    },
    [addTimer]
  );

  return {
    phase,
    displayedQuery,
    cursorVisible,
    visibleSearchQueries,
    visibleReferences,
    displayedCode,
    codeLanguage,
    exampleTitle,
    explanation,
    start,
    reset,
    activeSession,
    language: activeSession?.language ?? null,
  };
}
